import { alpha, Box, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { prepareFormElementProps } from 'components/common/form/formTools';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { components } from 'react-select';
import IconMi from 'components/common/icon/IconMi';
import { useAppBuilderContext } from 'app/app/editor/context';
import { getNodeByUid } from 'app/app/editor/page/getNodeByUid';
export const themeTokenToCssVar: Record<string, string> = {
    'primary.main': 'var(--theme-palette-primary-main)',
    'text.primary': 'var(--theme-palette-text-primary)',
    'background.default': 'var(--theme-palette-background-default)',
    'success.main': 'var(--theme-palette-success-main)',
    'error.main': 'var(--theme-palette-error-main)',
    'warning.main': 'var(--theme-palette-warning-main)',
    'warningLow.main': 'var(--theme-palette-warningLow-main)',
    'component.tab.color': 'var(--theme-component-tab-color)',
    'component.primaryBtn.background': 'var(--theme-component-primary-btn-background)',
    'component.primaryBtn.color': 'var(--theme-component-primary-btn-color)',
    'component.topbar.background': 'var(--theme-component-topbar-background)',
    'component.topbar.color': 'var(--theme-component-topbar-color)',
};
interface ColorFieldProps {
    formState: any;
    propertyName: string;
    onChangeStyle: (value: any) => void;
    uid: string;
}
const colorOptionsListTheme = [
    { label: 'Primary', tokenPath: 'primary.main' },
    { label: 'Text', tokenPath: 'text.primary' },
    { label: 'Background', tokenPath: 'background.default' },
    { label: 'Accept', tokenPath: 'success.main' },
    { label: 'Alert: High', tokenPath: 'error.main' },
    { label: 'Alert: Medium', tokenPath: 'warning.main' },
    { label: 'Alert: Low', tokenPath: 'warningLow.main' },
    { label: 'Tab', tokenPath: 'component.tab.color' },
    { label: 'Primary Button', tokenPath: 'component.primaryBtn.background' },
    { label: 'Primary Button Text', tokenPath: 'component.primaryBtn.color' },
    { label: 'Top Bar', tokenPath: 'component.topbar.background' },
    { label: 'Top Bar Text', tokenPath: 'component.topbar.color' },
].map((o) => ({ ...o, value: themeTokenToCssVar[o.tokenPath] }));
const colorOptionsList = [
    {
        label: 'None',
        value: '',
    },
    {
        label: 'Custom',
        value: 'custom',
    },
];

const OptionComponent = (props: any) => {
    return (
        <components.Option {...props}>
            <Stack direction={'row'} spacing={0.5}>
                {props.value != '' && (
                    <>
                        {props.value == 'custom' ? (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '16px',
                                    height: '16px',
                                    fontSize: '16px',
                                }}
                            >
                                <IconMi icon={'color-picker'} />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: props.value,
                                    border: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                }}
                            ></Box>
                        )}
                    </>
                )}

                <Box>{props.label}</Box>
            </Stack>
        </components.Option>
    );
};
const SelectedValueComponent = (props: any) => {
    let label = props.data.label;
    if (props.data.value != '' && props.data.value != 'custom') {
        const groupIndex = props.options.findIndex((group: any) => {
            return group?.options && group.options.find((item: any) => item.value == props.data.value);
        });
        if (groupIndex !== -1) label = props.options[groupIndex].label + ':  ' + label;
    }
    return (
        <components.SingleValue {...props}>
            <Stack direction={'row'} spacing={0.5}>
                {props.data.value != '' && (
                    <>
                        {props.data.value == 'custom' ? (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '16px',
                                    height: '16px',
                                    fontSize: '16px',
                                }}
                            >
                                <IconMi icon={'color-picker'} />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: props.data.value,
                                    border: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                }}
                            ></Box>
                        )}
                    </>
                )}

                <Box>{label}</Box>
            </Stack>
        </components.SingleValue>
    );
};
export default function ColorField({ propertyName, formState, onChangeStyle = () => {}, uid }: ColorFieldProps) {
    const ctx = useAppBuilderContext();
    const node = uid == 'page' ? ctx.page.layout : getNodeByUid(ctx.page.layout, uid ?? '');
    const [currentColor, setCurrentColor] = useState<string>('');
    const currentColorRef = useRef('');
    const [colorOptionsListFull, setColorOptionsListFull] = useState<any>(colorOptionsList);

    const elementProps = useCustomSimplifiedForm({
        colorField: '#222222',
    });
    const formValueColor = elementProps.form.hookFormWatch('colorField');

    useEffect(() => {
        currentColorRef.current = currentColor;
    }, [currentColor]);

    useEffect(() => {
        let fullOptionsList: any = [...colorOptionsList];
        const colorVariables = (ctx.page?.cssVariables ?? []).filter((variable) => {
            return variable.type == 'color';
        });
        if (colorVariables.length) {
            const newVariableOptions = colorVariables.map((variable) => {
                return {
                    label: `${variable.title}`,
                    value: `var(--${variable.name})`,
                };
            });
            fullOptionsList.push({
                label: 'Style Variable',
                options: newVariableOptions,
            });
        }
        fullOptionsList.push({
            label: 'Theme Color',
            options: colorOptionsListTheme,
        });
        setColorOptionsListFull(fullOptionsList);

        if (node) {
            const rawVal = node?.params?.layout?.styles?.[propertyName];
            if (rawVal && typeof rawVal === 'object') {
                if (rawVal.source === 'theme') {
                    const opt = colorOptionsListTheme.find((o) => o.tokenPath === rawVal.value);
                    setCurrentColor(opt?.value ?? '');
                } else {
                    // source === 'parent': direct value (hex or CSS var)
                    if (rawVal.value?.startsWith('var(--')) {
                        setCurrentColor(rawVal.value);
                    } else {
                        elementProps.form.hookFormSetValue('colorField', rawVal.value);
                        setCurrentColor('custom');
                    }
                }
            } else {
                // Backward-compat: plain string
                const colorVal: string = rawVal ?? '';
                if (colorVal === '' || colorVal.startsWith('var(--')) {
                    setCurrentColor(colorVal);
                } else {
                    elementProps.form.hookFormSetValue('colorField', colorVal);
                    setCurrentColor('custom');
                }
            }
        }
    }, []);

    useEffect(() => {
        if (currentColorRef.current !== 'custom') return;
        onChangeStyle({ [propertyName]: { source: 'parent', value: formValueColor } });
    }, [formValueColor]);

    const handleChangeColorVariant = (value: any) => {
        setCurrentColor(value);
        if (value === '') {
            onChangeStyle({ [propertyName]: '' });
        } else if (value === 'custom') {
            onChangeStyle({ [propertyName]: { source: 'parent', value: formValueColor || '#222222' } });
        } else if (value.startsWith('var(--theme-')) {
            const opt = colorOptionsListTheme.find((o) => o.value === value);
            onChangeStyle({ [propertyName]: { source: 'theme', value: opt?.tokenPath ?? value } });
        } else {
            // CSS style variable
            onChangeStyle({ [propertyName]: { source: 'parent', value: value } });
        }
    };
    return (
        <>
            <ReactSelect
                className={'design-variant--secondary'}
                data={colorOptionsListFull}
                value={[currentColor]}
                update={handleChangeColorVariant}
                overrideComponents={{
                    Option: OptionComponent,
                    SingleValue: SelectedValueComponent,
                }}
            />
            {currentColor == 'custom' && (
                <Box sx={{ pt: 1 }}>
                    <ReactHookFormController
                        elementProps={prepareFormElementProps({
                            ...elementProps,
                            component: {
                                component: 'ColorPicker',
                                name: 'colorField',
                                designVariant: 'secondary',
                                props: {
                                    disableAlpha: false,
                                },
                            },
                        })}
                    />
                </Box>
            )}
        </>
    );
}
